import React from "react";
import { useParams } from "react-router";
import { Pagination as PaginationType } from "@sscs/types";
import {
  EmptyState,
  FilterBar,
  Page,
  PageNotFound,
  QueryResult,
  hasActiveFilters,
} from "@sscs/ui";
import { getClassName, getQueryStatus } from "@sscs/utils";
import { useAppDispatch } from "Hooks";
import InsightCard from "../../components/InsightCard/InsightCard";
import UnsavedChanges from "../../components/UnsavedChanges/UnsavedChanges";
import { INSIGHTS_SEARCH_SORT, UNSAVED_SEARCH } from "../../constants";
import useSearch from "../../hooks/useSearch";
import { clearGroupFilters, paginationUpdated, sortUpdated } from "../../store";
import { InsightsResults, InsightsSearchSortKeys } from "../../types";
import Filter from "./Filter";
import styles from "./search.module.scss";

const SORT_OPTIONS: ReadonlyArray<{
  property: InsightsSearchSortKeys;
  label: string;
}> = Object.entries(INSIGHTS_SEARCH_SORT).map(([property, label]) => ({
  property: property as InsightsSearchSortKeys,
  label,
}));

const Search = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { groupId, searchId } = useParams();
  const {
    canUpdateSearch,
    filters,
    isDirty,
    pagination,
    savedSearch,
    searchQueryResult,
    sort,
    handleFilterChange,
    handleUpdateFilter,
    handleSaveFilter,
  } = useSearch();
  // Keep cached data if fetching
  const searchResults: InsightsResults =
    (Object.keys(filters).length > 0 || savedSearch.isFetching) &&
    searchQueryResult.data
      ? searchQueryResult.data
      : {
          issues: [],
          match_count: 0,
          estimated_count: 0,
        };

  const handlePaginationChange = (newPagination: PaginationType) => {
    if (groupId) {
      dispatch(paginationUpdated({ groupId, pagination: newPagination }));
    }
  };

  if (searchId && savedSearch.isError) {
    return (
      <PageNotFound
        title="Selected search not found"
        byline="Try selecting a search from the sidebar"
      />
    );
  }

  const isResultsPending =
    searchQueryResult.isFetching || searchQueryResult.isLoading;
  // A capped result set keeps the "showing the first N of M" hint; the uncapped
  // case falls back to the plain "N results".
  const cappedResultsLabel =
    searchResults.match_count !== searchResults.estimated_count ? (
      <>
        Showing the first {searchResults.match_count.toLocaleString()} of{" "}
        {searchResults.estimated_count.toLocaleString()} results found. Apply
        more filters to narrow your search.
      </>
    ) : undefined;

  return (
    <QueryResult
      status={getQueryStatus({
        data: savedSearch.data,
        isFetching: savedSearch.isFetching,
        isLoading: savedSearch.isLoading,
        isError: savedSearch.isError,
        error: savedSearch.error,
      })}
      fetchingClassName={styles.fetching}
    >
      <Page.Root>
        <Page.Header
          className={styles.header}
          title={
            <span
              className={getClassName({
                [styles["new-search"]]: !savedSearch.data,
              })}
            >
              {savedSearch.data?.name ?? UNSAVED_SEARCH}
            </span>
          }
          details={["Insights"]}
          actions={
            isDirty ? (
              <UnsavedChanges
                canUpdateSearch={canUpdateSearch}
                onSave={handleSaveFilter}
                onUpdate={handleUpdateFilter}
              />
            ) : undefined
          }
        />
        <Page.Filters
          className={styles.filters}
          found={{
            id: "insights-filters-clear",
            // Leave count/label undefined while the query is in flight so the
            // summary row keeps its height and fills in place when data lands.
            count: isResultsPending ? undefined : searchResults.match_count,
            label: isResultsPending ? undefined : cappedResultsLabel,
            initialFilters: savedSearch.data?.filters ?? {},
            activeFilters: filters,
            onClear: () => dispatch(clearGroupFilters(groupId)),
          }}
        >
          <FilterBar
            filters={[
              {
                id: "insights-filters-multistep",
                minWidth: "0",
                control: (
                  <Filter
                    filters={filters ?? {}}
                    onFilterChange={handleFilterChange}
                  />
                ),
              },
            ]}
            sort={{
              id: "insights-sort",
              options: SORT_OPTIONS,
              value: sort,
              onChange: (newSort) => {
                if (!groupId) return;

                dispatch(sortUpdated({ groupId, sort: newSort }));
              },
            }}
          />
        </Page.Filters>
        <Page.Body
          status={getQueryStatus({
            ...searchQueryResult,
            data: searchResults,
          })}
          fetchingClassName={styles.fetching}
        >
          {searchResults.issues.length > 0 ? (
            <div className={styles["results-wrapper"]}>
              {searchResults.issues.map((issue, index) => (
                <InsightCard key={issue.id} issue={issue} rowIndex={index} />
              ))}
            </div>
          ) : (
            !isResultsPending && (
              <EmptyState
                subject="insights"
                filters={{
                  active: hasActiveFilters(
                    savedSearch.data?.filters ?? {},
                    filters,
                  ),
                  onReset: () => dispatch(clearGroupFilters(groupId)),
                }}
              />
            )
          )}
          <Page.Pagination
            id="insights-pagination"
            itemCount={searchResults?.match_count ?? 0}
            pagination={pagination}
            onChange={handlePaginationChange}
          />
        </Page.Body>
      </Page.Root>
    </QueryResult>
  );
};

export default Search;
